/**
 * Theme Store - UI Preferences and Theme Management
 *
 * Manages theme settings, layout preferences, and UI state.
 * Provides hooks for theme mode, presets, and UI actions.
 */

import type { BaseStore } from "../../core";
import { createStore, withLogging } from "../../core";
import type { ThemeMode, ThemePreset } from "../../types";

// Theme store state interface
interface ThemeState extends BaseStore {
  // Theme state
  mode: ThemeMode;
  preset: ThemePreset;
  systemTheme: ThemeMode;
  effectiveTheme: ThemeMode;

  // UI preferences
  isSidebarCollapsed: boolean;
  isCompactMode: boolean;
  areAnimationsEnabled: boolean;

  // Actions
  setMode: (mode: ThemeMode) => void;
  setPreset: (preset: ThemePreset) => void;
  setSystemTheme: (theme: ThemeMode) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setCompactMode: (compact: boolean) => void;
  setAnimationsEnabled: (enabled: boolean) => void;
  toggleSidebar: () => void;
  toggleCompactMode: () => void;
  toggleAnimations: () => void;
  reset: () => void;
}

// Helper function to detect system theme
const getSystemTheme = (): ThemeMode => {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return "light";
};

// Helper function to calculate effective theme
const calculateEffectiveTheme = (mode: ThemeMode, systemTheme: ThemeMode): ThemeMode => {
  return mode === "system" ? systemTheme : mode;
};

// Initial state
const initialMode: ThemeMode = "light";
const initialSystemTheme = getSystemTheme();
const initialState = {
  mode: initialMode,
  preset: "default" as ThemePreset,
  systemTheme: initialSystemTheme,
  effectiveTheme: calculateEffectiveTheme(initialMode, initialSystemTheme),
  isSidebarCollapsed: false,
  isCompactMode: false,
  areAnimationsEnabled: true,
};

// Create the theme store
export const useThemeStore = createStore<ThemeState>(
  withLogging("ThemeStore", (set) => ({
    ...initialState,
    _initialized: false,
    _lastUpdated: Date.now(),

    // Theme actions
    setMode: (mode: ThemeMode) =>
      set((state) => {
        state.mode = mode;
        state.effectiveTheme = calculateEffectiveTheme(mode, state.systemTheme);
        state._lastUpdated = Date.now();
      }),

    setPreset: (preset: ThemePreset) =>
      set((state) => {
        state.preset = preset;
        state._lastUpdated = Date.now();
      }),

    setSystemTheme: (theme: ThemeMode) =>
      set((state) => {
        state.systemTheme = theme;
        state.effectiveTheme = calculateEffectiveTheme(state.mode, theme);
        state._lastUpdated = Date.now();
      }),

    // UI preference actions
    setSidebarCollapsed: (collapsed: boolean) =>
      set((state) => {
        state.isSidebarCollapsed = collapsed;
        state._lastUpdated = Date.now();
      }),

    setCompactMode: (compact: boolean) =>
      set((state) => {
        state.isCompactMode = compact;
        state._lastUpdated = Date.now();
      }),

    setAnimationsEnabled: (enabled: boolean) =>
      set((state) => {
        state.areAnimationsEnabled = enabled;
        state._lastUpdated = Date.now();
      }),

    // Toggle actions
    toggleSidebar: () =>
      set((state) => {
        state.isSidebarCollapsed = !state.isSidebarCollapsed;
        state._lastUpdated = Date.now();
      }),

    toggleCompactMode: () =>
      set((state) => {
        state.isCompactMode = !state.isCompactMode;
        state._lastUpdated = Date.now();
      }),

    toggleAnimations: () =>
      set((state) => {
        state.areAnimationsEnabled = !state.areAnimationsEnabled;
        state._lastUpdated = Date.now();
      }),

    // Reset to defaults
    reset: () =>
      set((state) => {
        Object.assign(state, initialState);
        state._initialized = false;
        state._lastUpdated = Date.now();
      }),
  })),
  {
    name: "echo-theme-store",
    persist: true,
    version: 1,
    partialize: (state) => ({
      mode: state.mode,
      preset: state.preset,
      isSidebarCollapsed: state.isSidebarCollapsed,
      isCompactMode: state.isCompactMode,
      areAnimationsEnabled: state.areAnimationsEnabled,
    }),
    onRehydrateStorage: () => (state: ThemeState | null) => {
      if (state) {
        // Update system theme and effective theme on rehydration
        const systemTheme = getSystemTheme();
        state.systemTheme = systemTheme;
        state.effectiveTheme = calculateEffectiveTheme(state.mode, systemTheme);
      }
    },
  },
);

// Set up system theme listener
if (typeof window !== "undefined" && window.matchMedia) {
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  mediaQuery.addEventListener("change", (e) => {
    const systemTheme = e.matches ? "dark" : "light";
    useThemeStore.getState().setSystemTheme(systemTheme);
  });
}

// ===== SIMPLIFIED DOMAIN HOOK =====
/**
 * Get theme store state (simplified access)
 * @returns Complete theme state
 */
export const useTheme = () => useThemeStore((state) => state);

// ===== DATA SELECTORS =====
/**
 * Get current theme mode (light, dark, or system)
 */
export const useThemeMode = () => useThemeStore((state) => state.mode);

/**
 * Get current theme preset (e.g., default, blue)
 */
export const useThemePreset = () => useThemeStore((state) => state.preset);

// ===== STATE SELECTORS =====
/**
 * Get sidebar collapsed state
 */
export const useIsSidebarCollapsed = () => useThemeStore((state) => state.isSidebarCollapsed);

/**
 * Get compact mode state
 */
export const useIsCompactMode = () => useThemeStore((state) => state.isCompactMode);

/**
 * Get animations enabled state
 */
export const useAreAnimationsEnabled = () => useThemeStore((state) => state.areAnimationsEnabled);

// ===== COMPUTED SELECTORS =====
/**
 * Get the effective theme (light or dark), considering system preference
 */
export const useEffectiveTheme = () => useThemeStore((state) => state.effectiveTheme);

/**
 * Check if the effective theme is dark
 */
export const useIsDarkTheme = () => useThemeStore((state) => state.effectiveTheme === "dark");

/**
 * Get theme-related CSS classes for styling
 */
export const useThemeClasses = () =>
  useThemeStore((state) => ({
    theme: state.effectiveTheme,
    preset: state.preset,
    compact: state.isCompactMode,
    "sidebar-collapsed": state.isSidebarCollapsed,
    "animations-disabled": !state.areAnimationsEnabled,
  }));

// ===== ACTION HOOKS =====
/**
 * Get theme and UI action functions
 */
export const useThemeActions = () =>
  useThemeStore((state) => ({
    setMode: state.setMode,
    setPreset: state.setPreset,
    setSidebarCollapsed: state.setSidebarCollapsed,
    setCompactMode: state.setCompactMode,
    setAnimationsEnabled: state.setAnimationsEnabled,
    toggleSidebar: state.toggleSidebar,
    toggleCompactMode: state.toggleCompactMode,
    toggleAnimations: state.toggleAnimations,
    reset: state.reset,
  }));
